/**
 * Logical ordering for apparel/product size labels.
 *
 * Variation sizes come from NetSuite as free-form strings, and the vocabulary
 * is not fixed: some records use "S/M/L/XL", others use "SM/MD/LG/XL", and a
 * few use numeric (waist/shoe) sizes or one-off labels like "One Size". We
 * can't rely on the data arriving pre-sorted, and plain alphabetical order is
 * wrong ("LG" < "MD" < "SM" < "XL"). This maps the common size vocabularies to
 * a canonical rank so chips render smallest → largest, and falls back
 * gracefully (numeric, then alphabetical) for anything unrecognised.
 */

// Canonical rank for known alpha size tokens. Lower = smaller.
const SIZE_RANK = {
  XXXS: 0,
  XXS: 5,
  XS: 10,
  S: 20,
  SM: 20,
  SML: 20,
  SMALL: 20,
  M: 30,
  MD: 30,
  MED: 30,
  MEDIUM: 30,
  L: 40,
  LG: 40,
  LRG: 40,
  LARGE: 40,
  XL: 50,
  XLARGE: 50,
  XXL: 60,
  "2XL": 60,
  XXXL: 70,
  "3XL": 70,
  XXXXL: 80,
  "4XL": 80,
  XXXXXL: 90,
  "5XL": 90,
};

// Labels that should always sort last (they aren't a point on the size scale).
const ONE_SIZE_RANK = 10000;

const normalize = (raw) =>
  String(raw == null ? "" : raw)
    .trim()
    .toUpperCase()
    .replace(/\s+/g, " ");

/**
 * Returns a numeric sort key for a single size label. Smaller sizes get smaller
 * keys. Unknown labels are pushed after known ones and ordered numerically then
 * alphabetically so the result is at least stable and predictable.
 */
export const sizeSortKey = (raw) => {
  const s = normalize(raw);
  if (!s) return ONE_SIZE_RANK + 1;

  // "One Size", "One Size Fits All", "OS", "OSFA" → always last.
  if (s === "OS" || s === "OSFA" || s.startsWith("ONE SIZE")) {
    return ONE_SIZE_RANK;
  }

  // Known alpha sizes (strip spaces so "2 XL" == "2XL").
  const compact = s.replace(/\s+/g, "");
  if (compact in SIZE_RANK) return SIZE_RANK[compact];

  // "nXL"/"nX" style (e.g. "6XL") not in the table → extrapolate.
  const nx = compact.match(/^(\d+)X L?$/) || compact.match(/^(\d+)XL?$/);
  if (nx) return 50 + (Number(nx[1]) - 1) * 10;

  // Pure numeric sizes (waist, shoe, etc.) → order numerically, after alpha
  // sizes but before unknown text.
  const num = compact.match(/^\d+(\.\d+)?$/);
  if (num) return 1000 + Number(compact);

  // Unknown text label → after everything numeric, alphabetical among itself.
  return ONE_SIZE_RANK - 1;
};

/**
 * Returns a new array of size labels sorted into logical (smallest → largest)
 * order. Ties (including unknown labels) fall back to alphabetical so output is
 * deterministic. Does not mutate the input.
 */
export const sortSizes = (sizes) => {
  if (!Array.isArray(sizes)) return [];
  return [...sizes].sort((a, b) => {
    const ka = sizeSortKey(a);
    const kb = sizeSortKey(b);
    if (ka !== kb) return ka - kb;
    return normalize(a).localeCompare(normalize(b));
  });
};

export default sortSizes;
